import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { PublishedGameFull } from '../types';
import { Scoreboard } from './Scoreboard';
import { GameLog } from './GameLog';
import { PlayerStats } from './PlayerStats';

export function HistoricalGameViewer() {
  const { gameId } = useParams<{ gameId: string }>();
  const [gameData, setGameData] = useState<PublishedGameFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'scoreboard' | 'playbyplay'>('scoreboard');

  useEffect(() => {
    const fetchGame = async () => {
      if (!gameId) {
        setError('No game ID provided');
        setLoading(false);
        return;
      }

      try {
        const baseUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${baseUrl}/api/published/${gameId}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Game not found');
          }
          throw new Error('Failed to load game');
        }
        const data = await response.json();
        setGameData(data);
        setError(null);
      } catch (err) {
        console.error('Game fetch error:', err);
        setError(err instanceof Error ? err.message : 'Unable to load game');
      } finally {
        setLoading(false);
      }
    };

    fetchGame();
  }, [gameId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="animate-spin text-4xl mb-4">🏀</div>
          <p className="text-gray-600">Loading game...</p>
        </div>
      </div>
    );
  }

  if (error || !gameData) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-4xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Game Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'This game may have been removed.'}</p>
          <Link
            to="/"
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const gameDate = new Date(gameData.gameDate).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const getPeriodLabel = (): string => {
    if (gameData.period <= 4) return 'Final';
    return `Final/OT${gameData.period - 4 > 1 ? gameData.period - 4 : ''}`;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/" className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <span className="text-xl sm:text-2xl">🏀</span>
            <div>
              <h1 className="text-sm sm:text-lg font-semibold text-gray-800">Game Recap</h1>
              <p className="text-xs text-gray-500">{gameDate}</p>
            </div>
          </div>
          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded font-medium">
            {getPeriodLabel()}
          </span>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto p-2 sm:p-4">
        {/* Scoreboard */}
        <div className="mb-3 sm:mb-6">
          <Scoreboard
            homeTeam={gameData.homeTeam}
            awayTeam={gameData.awayTeam}
            period={gameData.period}
            clockTime={null}
            possessionArrow={null}
            isFinal={true}
          />
        </div>

        {/* Mobile tabs */}
        <div className="md:hidden mb-2">
          <div className="flex bg-white rounded-lg shadow p-1">
            <button
              onClick={() => setActiveTab('scoreboard')}
              className={`flex-1 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                activeTab === 'scoreboard'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Stats
            </button>
            <button
              onClick={() => setActiveTab('playbyplay')}
              className={`flex-1 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                activeTab === 'playbyplay'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Play-by-Play
            </button>
          </div>
        </div>

        {/* Desktop: Two-column layout */}
        <div className="hidden md:grid md:grid-cols-2 gap-6 h-[500px]">
          <PlayerStats homeTeam={gameData.homeTeam} awayTeam={gameData.awayTeam} />
          <GameLog entries={gameData.gameLog} />
        </div>

        {/* Mobile: Tab content */}
        <div className="md:hidden h-[350px]">
          {activeTab === 'scoreboard' ? (
            <PlayerStats homeTeam={gameData.homeTeam} awayTeam={gameData.awayTeam} />
          ) : (
            <GameLog entries={gameData.gameLog} />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center py-2 sm:py-4 text-gray-400 text-xs sm:text-sm">
        <Link to="/" className="hover:text-gray-600">
          ← Back to Dashboard
        </Link>
      </footer>
    </div>
  );
}
